import React, { useMemo, useState } from 'react';
import { Button, Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import type { FinanceObligationReadItem, Payment } from '../../store/api/financeApi';
import type { GovernanceOverviewResponse } from '../../store/api/managementApi';
import type { WorkspaceRoute, WorkspaceTask } from '../../store/api/workspaceApi';
import { getPaymentFlowLabel } from '../../utils/paymentTypeLabels';
import type { DashboardRoleKind } from '../../utils/roleAwareEntityNavigation';
import { applicationCardPath, routeCardPath } from '../../utils/roleAwareEntityNavigation';
import dayjs from 'dayjs';

const { Text } = Typography;

/** Человекочитаемые статусы оплат/обязательств (без сырых enum). */
export function financeStatusRu(status: string): string {
  const s = String(status || '').toUpperCase();
  const map: Record<string, string> = {
    OPEN: 'Открыто',
    PENDING: 'Ожидает оплаты',
    PAID: 'Оплачено',
    PARTIALLY_PAID: 'Частично оплачено',
    NOT_PAID: 'Не оплачено',
    OVERDUE: 'Просрочено',
    CLOSED: 'Закрыто',
    PARTIAL: 'Частично оплачено',
    CANCELLED: 'Отменено',
    CONFIRMED: 'Подтверждено',
    REJECTED: 'Отклонено',
    DRAFT: 'Черновик',
  };
  return map[s] || (status ? status : '—');
}

function formatRub(amount: number): string {
  const n = Number.isFinite(amount) ? Math.round(amount) : 0;
  return `${n.toLocaleString('ru-RU')} ₽`;
}

function routeNum(route: WorkspaceRoute): string {
  return route.number?.trim() ? String(route.number) : `№${route.id}`;
}

type TimeBucket = 'overdue' | 'today' | 'soon' | 'nodue';

function obligationTimeBucket(o: FinanceObligationReadItem): TimeBucket {
  const rem = Number(o.remainingAmount ?? 0);
  if (rem <= 0.005) return 'nodue';
  const dl = o.daysLeft;
  if (dl != null && dl < 0) return 'overdue';
  if (o.dueDate) {
    const d = dayjs(o.dueDate).startOf('day');
    const t = dayjs().startOf('day');
    if (!d.isValid()) return 'nodue';
    if (d.isBefore(t, 'day')) return 'overdue';
    if (d.isSame(t, 'day')) return 'today';
    return 'soon';
  }
  return 'nodue';
}

function dueHintForObl(o: FinanceObligationReadItem): string {
  const rem = Number(o.remainingAmount ?? 0);
  if (rem <= 0.005) return '—';
  const dl = o.daysLeft;
  if (o.dueDate) {
    const d = dayjs(o.dueDate);
    if (!d.isValid()) return 'без срока';
    const dateStr = d.format('DD.MM');
    if (dl != null && dl < 0) return `просрочено ${Math.abs(dl)} дн. · до ${dateStr}`;
    if (dl === 0) return 'сегодня';
    if (dl != null && dl > 0) return `через ${dl} дн. · до ${dateStr}`;
    return `до ${dateStr}`;
  }
  if (dl != null && dl < 0) return `просрочено ${Math.abs(dl)} дн.`;
  return 'без срока';
}

function dueTagLine(o: FinanceObligationReadItem): { line: string; urgent: boolean } {
  const b = obligationTimeBucket(o);
  if (b === 'overdue') {
    const d = o.daysLeft != null ? Math.abs(o.daysLeft) : '?';
    return { line: `Просрочено ${d} дн.`, urgent: true };
  }
  if (b === 'today') return { line: 'Сегодня', urgent: false };
  if (b === 'soon') return { line: dueHintForObl(o), urgent: false };
  return { line: 'Без срока', urgent: false };
}

function sortObligationsForDisplay(list: FinanceObligationReadItem[]): FinanceObligationReadItem[] {
  const rank = (o: FinanceObligationReadItem) => {
    const b = obligationTimeBucket(o);
    if (b === 'overdue') return 0;
    if (b === 'today') return 1;
    if (b === 'soon') return 2;
    return 3;
  };
  return [...list].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const da = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    const db = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    return da - db;
  });
}

export type FinanceContextOpenPayload = { kind: 'route' | 'application'; id: number };

function resolveFinanceEntityOpen(
  applicationId: number | null | undefined,
  routeId: number | null | undefined,
  navigate: (path: string) => void,
  roleKind: DashboardRoleKind,
  onOpenFinanceContext?: (p: FinanceContextOpenPayload) => void
): void {
  if (onOpenFinanceContext) {
    if (applicationId != null) {
      onOpenFinanceContext({ kind: 'application', id: applicationId });
      return;
    }
    if (routeId != null) {
      onOpenFinanceContext({ kind: 'route', id: routeId });
      return;
    }
    return;
  }
  if (applicationId != null) navigate(applicationCardPath(applicationId, roleKind));
  else if (routeId != null) navigate(routeCardPath(routeId, roleKind));
}

export type FinanceProblemItem = {
  key: string;
  color: string;
  title: string;
  hint?: string;
  task?: WorkspaceTask | null;
  onNavigate?: () => void;
};

export function buildFinanceProblemItems(params: {
  incoming: FinanceObligationReadItem[];
  outgoing: FinanceObligationReadItem[];
  routes: WorkspaceRoute[];
  governance: GovernanceOverviewResponse | undefined;
  navigate: (path: string) => void;
  roleKind: DashboardRoleKind;
  onOpenFinanceContext?: (p: FinanceContextOpenPayload) => void;
}): FinanceProblemItem[] {
  const { incoming, outgoing, routes, governance, navigate, roleKind, onOpenFinanceContext } = params;
  const items: FinanceProblemItem[] = [];

  const overdueOut = outgoing.filter((o) => Number(o.remainingAmount ?? 0) > 0.005 && obligationTimeBucket(o) === 'overdue');
  overdueOut.slice(0, 4).forEach((o) => {
    items.push({
      key: `po-${o.id}`,
      color: 'orange',
      title: `Просрочена оплата перевозчику ${o.counterpartyName || 'контрагент'}`,
      hint: `${formatRub(Number(o.remainingAmount ?? 0))} · ${dueHintForObl(o)}`,
      onNavigate: () => resolveFinanceEntityOpen(o.applicationId, o.routeId, navigate, roleKind, onOpenFinanceContext),
    });
  });

  const overdueIn = incoming.filter((o) => Number(o.remainingAmount ?? 0) > 0.005 && obligationTimeBucket(o) === 'overdue');
  overdueIn.slice(0, 4).forEach((o) => {
    items.push({
      key: `ri-${o.id}`,
      color: 'orange',
      title: `Клиент ${o.counterpartyName || '—'} просрочил оплату`,
      hint: `${formatRub(Number(o.remainingAmount ?? 0))} · ${dueHintForObl(o)}`,
      onNavigate: () => resolveFinanceEntityOpen(o.applicationId, o.routeId, navigate, roleKind, onOpenFinanceContext),
    });
  });

  const anomalies = governance?.financeGovernance?.reconciliationAnomalies ?? 0;
  if (anomalies > 0) {
    items.push({
      key: 'recon',
      color: 'gold',
      title: 'Обнаружены расхождения по суммам — проверьте сверки с контрагентами',
      hint: `Найдено ситуаций: ${anomalies}`,
      onNavigate: () => navigate('/finance'),
    });
  }

  routes.forEach((route) => {
    const p = route.completionProjection;
    if (p?.physicalComplete && !p?.financialComplete) {
      items.push({
        key: `nfc-${route.id}`,
        color: 'gold',
        title: `Маршрут ${routeNum(route)} завершён по рейсу, но финансово не закрыт`,
        onNavigate: () => resolveFinanceEntityOpen(null, route.id, navigate, roleKind, onOpenFinanceContext),
      });
    }
  });

  const uniq = new Map<string, FinanceProblemItem>();
  items.forEach((it) => {
    if (!uniq.has(it.key)) uniq.set(it.key, it);
  });
  return Array.from(uniq.values()).slice(0, 10);
}

export function buildLogistFinanceBlockers(routes: WorkspaceRoute[], allTasks: WorkspaceTask[]): FinanceProblemItem[] {
  const items: FinanceProblemItem[] = [];
  routes.forEach((route) => {
    const p = route.completionProjection;
    if (p?.physicalComplete && !p?.financialComplete) {
      items.push({
        key: `l-nfc-${route.id}`,
        color: 'orange',
        title: `Маршрут ${routeNum(route)}: рейс завершён — ожидается финансовое закрытие`,
        hint: 'Согласование расходов или документы — уточните у бухгалтерии',
      });
    }
  });
  const expOnRoutes = allTasks.filter(
    (t) => String(t.taskType || '').toUpperCase() === 'EXPENSE_CONFIRMATION' && t.routeId != null
  );
  expOnRoutes.slice(0, 5).forEach((t) => {
    items.push({
      key: `l-exp-${t.id}`,
      color: 'gold',
      title: `По маршруту №${t.routeId} ожидается согласование расходов`,
      hint: 'После подтверждения бухгалтером рейс можно закрывать по финансам',
    });
  });
  const m = new Map<string, FinanceProblemItem>();
  items.forEach((it) => m.set(it.key, it));
  return Array.from(m.values()).slice(0, 6);
}

type KpiStripProps = {
  payments: Payment[];
  outgoingObligations: FinanceObligationReadItem[];
  pendingExpenseConfirmations: number;
  loading?: boolean;
};

export const DashboardFinanceKpiStrip: React.FC<KpiStripProps> = ({
  payments,
  outgoingObligations,
  pendingExpenseConfirmations,
  loading,
}) => {
  const { inToday, outToday, overduePayCount } = useMemo(() => {
    const today = dayjs().startOf('day');
    let inSum = 0;
    let outSum = 0;
    for (const p of payments) {
      const pd = p.paymentDate ? dayjs(p.paymentDate).startOf('day') : null;
      if (!pd || !pd.isValid() || !pd.isSame(today, 'day')) continue;
      const label = getPaymentFlowLabel(p);
      const amt = Number(p.amountPaid ?? 0) || Number(p.amountToPay ?? 0);
      if (label.includes('Поступление')) inSum += amt;
      if (label.includes('Оплата перевозчику')) outSum += amt;
    }
    const overduePay = outgoingObligations.filter(
      (o) => Number(o.remainingAmount ?? 0) > 0.005 && obligationTimeBucket(o) === 'overdue'
    ).length;
    return { inToday: inSum, outToday: outSum, overduePayCount: overduePay };
  }, [payments, outgoingObligations]);

  return (
    <Card size="small" loading={loading} styles={{ body: { paddingTop: 8 } }} title="Кассовый фокус">
      <div
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 10,
          overflowX: 'auto',
          paddingBottom: 2,
        }}
      >
        <div style={{ flex: '1 0 120px', minWidth: 100 }}>
          <Statistic title="Поступления сегодня" value={inToday} formatter={(v) => formatRub(Number(v))} valueStyle={{ fontSize: 18 }} />
        </div>
        <div style={{ flex: '1 0 120px', minWidth: 100 }}>
          <Statistic title="Оплаты сегодня" value={outToday} formatter={(v) => formatRub(Number(v))} valueStyle={{ fontSize: 18 }} />
        </div>
        <div style={{ flex: '1 0 100px', minWidth: 88 }}>
          <Statistic title="Просроченные оплаты" value={overduePayCount} valueStyle={{ fontSize: 18, color: overduePayCount ? '#d46b08' : undefined }} />
        </div>
        <div style={{ flex: '1 0 100px', minWidth: 88 }}>
          <Statistic
            title="Требуют подтверждения"
            value={pendingExpenseConfirmations}
            valueStyle={{ fontSize: 18, color: pendingExpenseConfirmations ? '#d48806' : undefined }}
          />
        </div>
      </div>
    </Card>
  );
};

type ObligationListProps = {
  title: string;
  obligations: FinanceObligationReadItem[];
  loading?: boolean;
  navigate: (path: string) => void;
  roleKind: DashboardRoleKind;
  onOpenFinanceContext?: (p: FinanceContextOpenPayload) => void;
};

export type DashboardObligationBlockProps = Omit<ObligationListProps, 'title'>;

function ObligationGroupedCard({ title, obligations, loading, navigate, roleKind, onOpenFinanceContext }: ObligationListProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const PREVIEW = 5;

  const active = useMemo(
    () => sortObligationsForDisplay(obligations.filter((o) => Number(o.remainingAmount ?? 0) > 0.005)),
    [obligations]
  );

  const groups = useMemo(() => {
    const overdue = active.filter((o) => obligationTimeBucket(o) === 'overdue');
    const today = active.filter((o) => obligationTimeBucket(o) === 'today');
    const soon = active.filter((o) => obligationTimeBucket(o) === 'soon');
    const nodue = active.filter((o) => obligationTimeBucket(o) === 'nodue');
    return { overdue, today, soon, nodue };
  }, [active]);

  const renderGroup = (groupKey: string, label: string, list: FinanceObligationReadItem[]) => {
    if (!list.length) return null;
    const expanded = !!expandedGroups[groupKey];
    const display = expanded ? list : list.slice(0, PREVIEW);
    return (
      <div key={groupKey} style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
          {label}
        </Text>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {display.map((o) => {
            const due = dueTagLine(o);
            return (
              <Row
                key={o.id}
                gutter={[10, 6]}
                wrap={false}
                align="top"
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: due.urgent ? 'rgba(255, 77, 79, 0.06)' : 'var(--ant-color-fill-quaternary, #fafafa)',
                  border: `1px solid ${due.urgent ? 'rgba(255, 77, 79, 0.35)' : 'var(--ant-color-border-secondary, #f0f0f0)'}`,
                }}
              >
                <Col flex="auto" style={{ minWidth: 0 }}>
                  <Text style={{ fontSize: 18, fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                    {formatRub(Number(o.remainingAmount ?? 0))}
                  </Text>
                  <Text type="secondary" ellipsis style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
                    {o.counterpartyName || '—'}
                  </Text>
                  <Space size={6} wrap style={{ marginTop: 4 }}>
                    <Tag color={due.urgent ? 'error' : 'default'} style={{ margin: 0 }}>
                      {due.urgent ? `🔴 ${due.line}` : due.line}
                    </Tag>
                    <Tag style={{ margin: 0 }}>{financeStatusRu(o.status)}</Tag>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                    {o.applicationInternalNumber ? `Заявка ${o.applicationInternalNumber}` : ''}
                    {o.applicationInternalNumber && o.routeId ? ' · ' : ''}
                    {o.routeId ? `Маршрут №${o.routeId}` : ''}
                  </Text>
                </Col>
                <Col flex="none">
                  <Button
                    type="primary"
                    size="small"
                    ghost
                    onClick={() => resolveFinanceEntityOpen(o.applicationId, o.routeId, navigate, roleKind, onOpenFinanceContext)}
                  >
                    Открыть
                  </Button>
                </Col>
              </Row>
            );
          })}
        </Space>
        {list.length > PREVIEW ? (
          <Button
            type="link"
            size="small"
            style={{ paddingLeft: 0, marginTop: 4 }}
            onClick={() => setExpandedGroups((g) => ({ ...g, [groupKey]: !expanded }))}
          >
            {expanded ? 'Свернуть' : `Показать все (${list.length})`}
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <Card size="small" title={title} loading={loading} styles={{ body: { paddingTop: 8 } }}>
      {!active.length ? (
        <Text type="secondary">Нет открытых позиций по этому разделу.</Text>
      ) : (
        <>
          {renderGroup('overdue', 'Просрочено', groups.overdue)}
          {renderGroup('today', 'Сегодня', groups.today)}
          {renderGroup('soon', 'Ближайшие', groups.soon)}
          {renderGroup('nodue', 'Без срока', groups.nodue)}
        </>
      )}
    </Card>
  );
}

export const DashboardReceivablesBlock: React.FC<DashboardObligationBlockProps> = (props) => (
  <ObligationGroupedCard {...props} title="Ожидаем поступления" />
);

export const DashboardPayablesBlock: React.FC<DashboardObligationBlockProps> = (props) => (
  <ObligationGroupedCard {...props} title="Требуют оплаты" />
);

type ProblemsProps = {
  items: FinanceProblemItem[];
  onOpenTask: (t: WorkspaceTask) => void;
};

export const DashboardFinanceProblemsCard: React.FC<ProblemsProps> = ({ items, onOpenTask }) => (
  <Card
    size="small"
    title="Финансовые проблемы"
    styles={{ body: { paddingTop: 8 } }}
  >
    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
      Только срочные деньги и закрытие: просрочки, незакрытые рейсы, расхождения. Задачи по документам и расходам — в блоке «Финансовые задачи».
    </Text>
    {!items.length ? (
      <Text type="secondary">Критичных финансовых сигналов не найдено.</Text>
    ) : (
      <List
        size="small"
        dataSource={items}
        renderItem={(it) => (
          <List.Item style={{ padding: '6px 0', borderBlockEnd: 'none' }}>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Space align="start" size={8}>
                <Tag color={it.color} style={{ margin: 0 }}>
                  {it.color === 'red' ? 'Важно' : it.color === 'orange' ? 'Срочно' : 'Внимание'}
                </Tag>
                <Text strong style={{ flex: 1 }}>
                  {it.title}
                </Text>
              </Space>
              {it.hint ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {it.hint}
                </Text>
              ) : null}
              <Space>
                {it.task ? (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenTask(it.task!)}>
                    Выполнить
                  </Button>
                ) : null}
                {it.onNavigate ? (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={it.onNavigate}>
                    Перейти
                  </Button>
                ) : null}
              </Space>
            </Space>
          </List.Item>
        )}
      />
    )}
  </Card>
);

type LogistBlockersProps = {
  items: FinanceProblemItem[];
};

export const DashboardLogistFinanceBlockers: React.FC<LogistBlockersProps> = ({ items }) => {
  if (!items.length) return null;
  return (
    <Card size="small" title="Финансовые ожидания по вашим маршрутам" styles={{ body: { paddingTop: 8 } }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        Суммы и дебиторка скрыты. Здесь только то, что мешает закрытию рейса.
      </Text>
      <List
        size="small"
        dataSource={items}
        renderItem={(it) => (
          <List.Item style={{ padding: '4px 0', borderBlockEnd: 'none' }}>
            <Tag color={it.color}>{it.title}</Tag>
            {it.hint ? (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                {it.hint}
              </Text>
            ) : null}
          </List.Item>
        )}
      />
    </Card>
  );
};
